// The conversation with the passenger as lines to read (PO 2026-11-03: «es imposible de leer,
// es demasiada información… los últimos eventos del más reciente al más viejo»). Built from the
// case's notification row and the sub-case's last answer — no new read:
//   • one line per message we sent, its receipts (delivered · opened · click) as steps on it,
//     never as lines of their own;
//   • one line per visit of the passenger (opened the link › got into the app › saw the offer);
//   • their answer;
//   • the same line again and again (five SMS failing for want of a phone) is ONE line, ×5.
// Newest first. Pure: the card and the «Ver todo» dialog render the same lines.

import type {
  INotificationEngagementEvent,
  INotificationSendRow,
  INotificationStatusRow,
  NotificationChannel,
} from '@/lib/notifications/types';
import { CHANNEL_LABEL_KEY } from '@/lib/notifications/labels';
import { engagementMoments, readKind } from '@/lib/notifications/engagement';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { notificationFailureReason } from './pnr-steps';

export type TalkKind = 'sent' | 'failed' | 'passenger' | 'accepted' | 'declined' | 'notBought';

/** A receipt on a message we sent: delivered, opened, clicked. */
export interface ITalkStep {
  label: string;
  /** ISO-8601, when we know it (a delivery has no moment of its own). */
  at?: string | undefined;
  done: boolean;
}

export interface ITalkLine {
  kind: TalkKind;
  channel?: NotificationChannel | undefined;
  text: string;
  detail?: string | undefined;
  steps: ITalkStep[];
  /** ISO-8601 — the newest moment of the line. */
  at: string;
  /** Every moment the line stands for, oldest first (more than one = repeated). */
  times: string[];
  /** Failed for want of a phone / an email: the line offers the contact fix. */
  missingContact?: boolean | undefined;
}

export interface ITalkChip {
  key: string;
  label: string;
  tone: 'ok' | 'alert' | 'idle';
  channel?: NotificationChannel | undefined;
}

type T = (key: string, options?: Record<string, unknown>) => string;

const ACTION_KEY: Record<string, string> = {
  ACCEPT_OFFER: 'offer',
  CONTACT_DESK: 'desk',
  NONE: 'notice',
};

/** Two visits of the passenger further apart than this are two lines. */
const VISIT_GAP_MS = 15 * 60_000;

const STEP_KEY: Record<INotificationEngagementEvent['kind'], string> = {
  LINK_OPENED: 'card.talk.opened',
  APP_OPENED: 'card.talk.appOpened',
  OFFER_VIEWED: 'card.talk.viewed',
};

const ms = (iso: string): number => {
  const v = Date.parse(iso);
  return Number.isFinite(v) ? v : 0;
};

/** Advisories the line names by what they said (PO 2026-11-03: «avisarles… que la oferta no
 *  está más disponible») — any other is «un aviso». */
const ADVISORY_KEY: Record<string, string> = {
  OFFER_EXPIRED: 'offerExpired',
};

function what(send: Pick<INotificationSendRow, 'requestedAction' | 'advisoryKind'>, t: T): string {
  const advisory = send.advisoryKind ? ADVISORY_KEY[send.advisoryKind] : undefined;
  if (advisory) return t(`card.talk.what.${advisory}`);
  return t(`card.talk.what.${ACTION_KEY[send.requestedAction ?? 'NONE'] ?? 'notice'}`);
}

function sendLine(send: INotificationSendRow, t: T): ITalkLine {
  const channel = t(CHANNEL_LABEL_KEY[send.channel]);
  const at = send.sentAt ?? send.lastEventAt;
  if (send.deliveryState === 'failed') {
    const reason = notificationFailureReason(send.failureReason);
    const missingContact = reason === 'no_phone_on_file' || reason === 'no_email_on_file';
    return {
      kind: 'failed',
      channel: send.channel,
      text: t('card.talk.notSent', { channel, what: what(send, t) }),
      detail: missingContact ? t(`card.talk.why.${reason}`) : t(`notification.current.failure.${reason}`),
      steps: [],
      at,
      times: [at],
      missingContact,
    };
  }
  const steps: ITalkStep[] = [];
  if (send.deliveryState !== 'unknown') {
    steps.push({ label: t(`card.talk.receipt.${send.deliveryState}`), done: send.deliveryState === 'delivered' });
  }
  if (send.readAt) steps.push({ label: t(`card.talk.receipt.${readKind(send.channel)}`), at: send.readAt, done: true });
  if (send.clickedAt) steps.push({ label: t('card.talk.receipt.clicked'), at: send.clickedAt, done: true });
  return {
    kind: 'sent',
    channel: send.channel,
    text: t('card.talk.sentTo', { channel, what: what(send, t) }),
    steps,
    at,
    times: [at],
  };
}

/** The passenger's visits: what they did, step by step, within one sitting. */
function visitLines(row: INotificationStatusRow | null | undefined, t: T): ITalkLine[] {
  const moments: INotificationEngagementEvent[] = row?.engagement?.length
    ? engagementMoments(row.engagement)
    : row?.linkOpenedAt
      ? [{ kind: 'LINK_OPENED', at: row.linkOpenedAt }]
      : [];
  const visits: INotificationEngagementEvent[][] = [];
  for (const m of moments) {
    const visit = visits[visits.length - 1];
    const last = visit?.[visit.length - 1];
    if (visit && last && ms(m.at) - ms(last.at) <= VISIT_GAP_MS) visit.push(m);
    else visits.push([m]);
  }
  return visits.map((visit) => {
    const at = visit[visit.length - 1]!.at;
    return {
      kind: 'passenger' as const,
      text: visit.map((m) => t(STEP_KEY[m.kind])).join(' › '),
      steps: [],
      at,
      times: [at],
    };
  });
}

function sameLine(a: ITalkLine, b: ITalkLine): boolean {
  return (
    a.kind === b.kind &&
    a.text === b.text &&
    a.detail === b.detail &&
    a.steps.map((s) => s.label).join('|') === b.steps.map((s) => s.label).join('|')
  );
}

/**
 * The conversation, newest first. `answer` is the passenger's last answer to show as a line
 * (none when it is a callback request — that has its own attention line).
 */
export function conversationLines({
  row,
  answer,
  answerFlight,
  purchaseFailed,
  t,
}: {
  row: INotificationStatusRow | null | undefined;
  answer?: DisruptionPnrGroupRow['lastOfferResponse'] | undefined;
  /** The flight their answer was about («Aceptó LA0063»). */
  answerFlight?: string | undefined;
  /** The purchase that failed after they accepted — why the ticket did not come (PO 2026-11-03:
   *  «el pasajero aceptó, pero ¿por qué no se emitió?»). */
  purchaseFailed?: { at: string; why?: string | undefined } | undefined;
  t: T;
}): ITalkLine[] {
  const sends = row?.sends ?? [];
  const lines: ITalkLine[] = sends.map((s) => sendLine(s, t));
  if (sends.length === 0 && row?.lastEventAt && row.channel) {
    // An older BFF: only the row's own last send.
    lines.push(
      sendLine(
        {
          notificationUrn: row.notificationUrn ?? 'row',
          channel: row.channel,
          deliveryState: row.deliveryState,
          ...(row.requestedAction ? { requestedAction: row.requestedAction } : {}),
          lastEventAt: row.lastEventAt,
          ...(row.failureReason ? { failureReason: row.failureReason } : {}),
          ...(row.readAt ? { readAt: row.readAt } : {}),
          ...(row.clickedAt ? { clickedAt: row.clickedAt } : {}),
        },
        t,
      ),
    );
  }
  lines.push(...visitLines(row, t));
  if (answer) {
    const reason = answer.reason?.trim();
    lines.push(
      answer.response === 'ACCEPTED'
        ? {
            kind: 'accepted',
            text: answerFlight ? t('card.talk.acceptedFlight', { flight: answerFlight }) : t('card.talk.accepted'),
            steps: [],
            at: answer.at,
            times: [answer.at],
          }
        : {
            kind: 'declined',
            text: reason ? t('card.talk.declinedWhy', { reason }) : t('card.talk.declined'),
            steps: [],
            at: answer.at,
            times: [answer.at],
          },
    );
  }
  if (answer?.response === 'ACCEPTED' && purchaseFailed) {
    lines.push({
      kind: 'notBought',
      text: answerFlight ? t('card.talk.notBoughtFlight', { flight: answerFlight }) : t('card.talk.notBought'),
      detail: purchaseFailed.why,
      steps: [],
      at: purchaseFailed.at,
      times: [purchaseFailed.at],
    });
  }
  // Oldest first to fold the repeats in the order they happened, then newest first to read.
  const ordered = lines.map((l, i) => ({ l, i })).sort((a, b) => ms(a.l.at) - ms(b.l.at) || a.i - b.i);
  const folded: ITalkLine[] = [];
  for (const { l } of ordered) {
    const prev = folded[folded.length - 1];
    if (prev && sameLine(prev, l)) {
      prev.times.push(l.at);
      prev.at = l.at;
      prev.steps = l.steps;
    } else {
      folded.push({ ...l, times: [...l.times] });
    }
  }
  return folded.reverse();
}

/**
 * Which channels reach the passenger — the newest send on each decides — and which contact is
 * missing. Worded once here instead of «Email ✓ · SMS ✗ · sin teléfono» under the lines.
 */
export function channelChips({
  sends,
  contact,
  t,
}: {
  sends: readonly INotificationSendRow[];
  contact?: { phone?: boolean; email?: boolean } | null | undefined;
  t: T;
}): ITalkChip[] {
  const newest = new Map<NotificationChannel, INotificationSendRow>();
  for (const s of sends) {
    const seen = newest.get(s.channel);
    if (!seen || ms(s.sentAt ?? s.lastEventAt) > ms(seen.sentAt ?? seen.lastEventAt)) newest.set(s.channel, s);
  }
  const chips: ITalkChip[] = [...newest.values()]
    .sort((a, b) => ms(b.sentAt ?? b.lastEventAt) - ms(a.sentAt ?? a.lastEventAt))
    .map((s) => {
      const fails = s.deliveryState === 'failed';
      return {
        key: s.channel,
        channel: s.channel,
        label: t(fails ? 'card.talk.channelFails' : 'card.talk.channelWorks', { channel: t(CHANNEL_LABEL_KEY[s.channel]) }),
        tone: fails ? ('alert' as const) : ('ok' as const),
      };
    });
  if (contact && !contact.phone) chips.push({ key: 'noPhone', label: t('card.talk.noPhone'), tone: 'idle' });
  if (contact && !contact.email) chips.push({ key: 'noEmail', label: t('card.talk.noEmail'), tone: 'idle' });
  return chips;
}
